import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, ClipboardCheck, Loader2, PartyPopper } from "lucide-react";
import { Reveal, SectionHeading, EASE, scrollToSection } from "./Reveal";
import { api, formatApiErrorDetail } from "../lib/api";
import { ASSESSMENT_STEPS } from "../data/content";

const OPTIONS = [
  [
    "Premium / luxury womenswear for metro Tier-1 audiences",
    "Mid-market D2C brand selling across Tier 1-2",
    "Value / mass brand focused on Tier 2-3",
    "We are still defining our target audience",
  ],
  [
    "Under 100 styles, 70%+ sell-through",
    "100-300 styles, 50-70% sell-through",
    "300-600 styles, 35-50% sell-through",
    "600+ styles, under 35% sell-through",
  ],
  [
    "In-house trend team with India-specific research",
    "Global forecast tools (WGSN etc.) adapted internally",
    "Buyer / vendor suggestions and market scans",
    "Founder intuition and instinct",
  ],
  [
    "Locked a season ahead, under 2% delays",
    "Seasonal calendar with occasional 1-2 week delays",
    "Frequent 4-6 week delays and festival rushes",
    "We don't run a formal calendar",
  ],
  [
    "Reduce dead inventory and improve margins",
    "Scale the design team with global workflows",
    "Launch our first production-ready collection",
    "Enter new categories or markets",
  ],
];

function ScoreDial({ score }) {
  const r = 84;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative w-[200px] h-[200px]" data-testid="assessment-score-dial">
      <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
        <circle cx="100" cy="100" r={r} fill="none" stroke="#e5e5e7" strokeWidth="10" />
        <motion.circle
          cx="100" cy="100" r={r} fill="none" stroke="#0066cc" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ - (score / 100) * circ }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.2 }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="text-5xl font-semibold tracking-tight text-[#1d1d1f]"
        >
          {score}
        </motion.span>
        <span className="text-xs uppercase tracking-[0.18em] text-[#86868b] mt-1">of 100</span>
      </div>
    </div>
  );
}

export default function Assessment() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", brand: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const pick = (idx) => {
    setAnswers((a) => {
      const next = [...a.slice(0, step), idx];
      return next;
    });
    setTimeout(() => setStep((s) => Math.min(s + 1, ASSESSMENT_STEPS.length)), 260);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/assessment", {
        name: contact.name,
        email: contact.email,
        brand: contact.brand,
        answers,
      });
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep(0); setAnswers([]); setResult(null); setContact({ name: "", email: "", brand: "" });
  };

  return (
    <section id="assessment" className="py-20 sm:py-28 lg:py-32 scroll-mt-16" data-testid="assessment-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        {!result && (
          <SectionHeading
            align="center"
            eyebrow="Free Brand Assessment"
            title="Score your brand's design readiness in 2 minutes"
            subtitle="Five questions. An instant score, a candid read on where you stand, and the exact engagement that fixes your biggest gap — emailed to you."
          />
        )}

        <div className="mt-12 sm:mt-16 bg-white rounded-[28px] border border-[#e5e5e7] shadow-[0_10px_40px_rgba(0,0,0,0.05)] overflow-hidden" data-testid="assessment-quiz-container">
          <AnimatePresence mode="wait">
            {result ? (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="p-8 sm:p-12"
                data-testid="assessment-result-card"
              >
                <div className="flex flex-col sm:flex-row items-center gap-10">
                  <ScoreDial score={result.score} />
                  <div className="flex-1 text-center sm:text-left">
                    <p className="flex items-center justify-center sm:justify-start gap-2 text-[#3d7a3d] text-sm font-medium">
                      <PartyPopper size={16} /> A copy is on its way to {contact.email}
                    </p>
                    <h3 className="mt-3 text-3xl font-semibold tracking-tight">{result.band}</h3>
                    <p className="mt-3 text-[#86868b] leading-relaxed">{result.summary}</p>
                  </div>
                </div>
                <div className="mt-10 rounded-2xl bg-[#f5f5f7] border border-[#e5e5e7] p-6 sm:p-8">
                  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[#86868b]">Recommended engagement</p>
                  <p className="mt-2 text-2xl font-semibold tracking-tight text-[#1d1d1f]">{result.recommendation}</p>
                  <p className="mt-2 text-[15px] text-[#1d1d1f]/70 leading-relaxed">{result.recommendation_desc}</p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      data-testid="assessment-book-cta"
                      onClick={() => scrollToSection("#booking")}
                      className="px-6 py-3 rounded-full bg-[#0066cc] text-white text-sm font-medium hover:bg-[#0077ed] transition-all flex items-center gap-2"
                    >
                      Discuss it on a discovery call <ArrowRight size={15} />
                    </button>
                    <button onClick={restart} className="px-6 py-3 rounded-full border border-[#d2d2d7] text-sm font-medium hover:bg-[#f5f5f7] transition-all">
                      Retake
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : step < ASSESSMENT_STEPS.length ? (
              <motion.div
                key={`q-${step}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="p-8 sm:p-12"
              >
                <div className="flex items-center justify-between text-xs font-medium text-[#86868b] uppercase tracking-[0.16em]">
                  <span>Question {step + 1} / {ASSESSMENT_STEPS.length}</span>
                  <ClipboardCheck size={16} className="text-[#0066cc]" />
                </div>
                <div className="mt-4 h-1 rounded-full bg-[#e5e5e7] overflow-hidden">
                  <motion.div
                    className="h-full bg-[#0066cc] rounded-full"
                    animate={{ width: `${(step / ASSESSMENT_STEPS.length) * 100}%` }}
                    transition={{ duration: 0.5, ease: EASE }}
                  />
                </div>
                <h3 className="mt-8 text-2xl sm:text-3xl font-semibold tracking-tight text-[#1d1d1f]">
                  {ASSESSMENT_STEPS[step].q}
                </h3>
                <div className="mt-8 grid gap-3">
                  {OPTIONS[step].map((label, i) => (
                    <button
                      key={label}
                      data-testid={`assessment-option-${step}-${i}`}
                      onClick={() => pick(i)}
                      className={`group flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-4 rounded-2xl border transition-all duration-200 ${
                        answers[step] === i
                          ? "border-[#0066cc] bg-[#0066cc]/[0.06] shadow-sm"
                          : "border-[#e5e5e7] hover:border-[#0066cc]/50 hover:bg-[#f5f5f7]"
                      }`}
                    >
                      <span className="text-[15px] sm:text-base font-medium text-[#1d1d1f]">{label}</span>
                      <span className={`w-6 h-6 rounded-full border flex items-center justify-center transition-all ${
                        answers[step] === i ? "bg-[#0066cc] border-[#0066cc] text-white" : "border-[#d2d2d7] text-transparent group-hover:border-[#0066cc]"
                      }`}>
                        <Check size={13} strokeWidth={3} />
                      </span>
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="mt-6 flex items-center gap-1.5 text-sm text-[#86868b] hover:text-[#1d1d1f] transition-colors"
                  >
                    <ArrowLeft size={15} /> Previous question
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.form
                key="contact"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45, ease: EASE }}
                onSubmit={submit}
                className="p-8 sm:p-12"
              >
                <p className="text-xs font-medium text-[#86868b] uppercase tracking-[0.16em]">Almost there</p>
                <h3 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight">Where should we send your result?</h3>
                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                  <input
                    required data-testid="assessment-name-input" placeholder="Your name"
                    value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    className="px-5 py-3.5 rounded-2xl border border-[#d2d2d7] bg-white text-[15px] focus:border-[#0066cc] outline-none transition-colors"
                  />
                  <input
                    required type="email" data-testid="assessment-email-input" placeholder="Email address"
                    value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    className="px-5 py-3.5 rounded-2xl border border-[#d2d2d7] bg-white text-[15px] focus:border-[#0066cc] outline-none transition-colors"
                  />
                  <input
                    data-testid="assessment-brand-input" placeholder="Brand / company (optional)"
                    value={contact.brand} onChange={(e) => setContact({ ...contact, brand: e.target.value })}
                    className="sm:col-span-2 px-5 py-3.5 rounded-2xl border border-[#d2d2d7] bg-white text-[15px] focus:border-[#0066cc] outline-none transition-colors"
                  />
                </div>
                {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    type="submit" disabled={loading} data-testid="assessment-next-btn"
                    className="px-7 py-3.5 rounded-full bg-[#0066cc] text-white font-medium text-sm hover:bg-[#0077ed] transition-all active:scale-[0.98] flex items-center gap-2 disabled:opacity-60"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <ClipboardCheck size={16} />}
                    {loading ? "Scoring..." : "Reveal my score"}
                  </button>
                  <button type="button" onClick={() => setStep(4)} className="flex items-center gap-1.5 text-sm text-[#86868b] hover:text-[#1d1d1f]">
                    <ArrowLeft size={15} /> Back
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
